import { FileText, PiggyBank } from 'lucide-react';
import { StatusBadge } from '@/components/StatusBadge';
import { SideDrawer } from '@/components/drawer/SideDrawer';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { SummaryStrip } from '@/components/drawer/SummaryStrip';
import { InfoMessage } from '@/components/drawer/InfoMessage';
import { PdfDocumentRow } from '@/components/form/PdfDocumentRow';
import { usePolicyPaymentsQuery } from '@/api/query/payment';
import { usePolicyInterestQuery, usePolicyQuery } from '@/api/query/policy';
import { useRefreshPolicyData } from '@/api/query/refresh';
import type { Member, Policy } from '@/types';
import { formatCurrency, formatPaymentDate } from '@/lib/payments';
import { SAVINGS_RATE } from '@/lib/policies';
import { ClosePolicyForm } from './ClosePolicyForm';
import { memberHeading } from '@/lib/utils';
import { SavingsHistory } from './SavingsHistory';

interface PolicySavingsProps {
  member: Member | null;
  policy: Policy | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatRupees = (amount: number) => `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

/**
 * Policy Savings drawer: the policy earns 6% a year on the amount paid until it is
 * closed. Closing pays the member the policy amount plus that interest.
 */
export function PolicySavings({ member, policy, open, onOpenChange }: PolicySavingsProps) {
  const refreshPolicyData = useRefreshPolicyData();
  const { data: details } = usePolicyQuery(policy?.id, open);
  const { data: interestRows = [] } = usePolicyInterestQuery(policy?.id, open);
  const { data: payments = [] } = usePolicyPaymentsQuery(policy?.id, open);

  const current = details ?? policy;
  const closure = details?.closure;
  const interestEarned = details?.interestEarned ?? 0;
  const totalValue = details?.totalValue ?? (current?.totalPaid ?? 0) + interestEarned;

  const handleClosed = async () => {
    await refreshPolicyData();
    onOpenChange(false);
  };

  return (
    <SideDrawer open={open} onOpenChange={onOpenChange} title="Policy Savings" member={member} tag={policy?.policyNo}>
      {current && (
        <>
          <DrawerSection
            icon={PiggyBank}
            title="Savings Status"
            description={
              closure
                ? `Closed on ${formatPaymentDate(closure.closedAt)} · interest stopped`
                : `Interest ${SAVINGS_RATE * 100}% per year on the amount paid, added every month`
            }
            aside={<StatusBadge status={current.status} />}
          >
            <SummaryStrip
              items={[
                { label: 'Policy Paid', value: formatCurrency(current.totalPaid) },
                { label: 'Interest Earned', value: formatRupees(closure?.interest ?? interestEarned), tone: 'text-teal-800' },
                {
                  label: closure ? 'Amount Paid Out' : 'Total Value',
                  value: formatRupees(closure?.payoutAmount ?? totalValue),
                },
              ]}
            />
          </DrawerSection>

          {closure ? (
            <DrawerSection
              icon={FileText}
              title="Policy Closed"
              description={`Closed on ${formatPaymentDate(closure.closedAt)} · ${formatRupees(closure.payoutAmount)} paid to the member (${closure.payoutType})`}
            >
              <dl className="divide-y divide-gray-100">
                {[
                  ['Close Type', 'Close Policy'],
                  ['Policy Paid', formatCurrency(closure.principal)],
                  ['Interest Paid', formatRupees(closure.interest)],
                  ...(closure.balanceDeducted > 0 ? [['Member Balance Deducted', `− ${formatRupees(closure.balanceDeducted)}`]] : []),
                  ['Total Paid to Member', formatRupees(closure.payoutAmount)],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-6 py-3">
                    <dt className="text-sm text-gray-500">{label}</dt>
                    <dd className="text-sm font-medium tabular-nums text-gray-900">{value}</dd>
                  </div>
                ))}
              </dl>
              <PdfDocumentRow
                name={`${current.policyNo}_closure.pdf`}
                url={closure.closureDocUrl}
                caption="Policy closure document"
              />
            </DrawerSection>
          ) : current.loan.eligibility === 'active' ? (
            <DrawerSection icon={FileText} title="Close Policy" description="Pay the policy amount and interest to the member">
              <InfoMessage>
                This policy has an active loan <b>{current.loan.loanNo}</b> with{' '}
                <b>{formatCurrency(current.loan.outstanding ?? 0)}</b> outstanding. The loan must be closed before the
                policy can be closed.
              </InfoMessage>
            </DrawerSection>
          ) : (
            <ClosePolicyForm
              key={current.id}
              policy={current}
              interestEarned={interestEarned}
              totalValue={totalValue}
              balanceDue={details?.memberBalanceDue ?? 0}
              onClosed={handleClosed}
            />
          )}

          <SavingsHistory
            key={current.id}
            subtitle={member ? `${memberHeading(member)} · ${current.policyNo}` : current.policyNo}
            interestRows={interestRows}
            payments={payments}
          />
        </>
      )}
    </SideDrawer>
  );
}
